import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  Users, Timer, BookOpen, ListChecks, CalendarDays, BadgeCheck,
  MessageSquare, User, GraduationCap, Award,
} from 'lucide-react';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  ServiceHero, JumpNav, SplitSection, StepsSection, CardsSection,
  PanelSection, CompareSection, ChipsSection, CtaBlock, FaqSection, RelatedPages,
  type FaqItem, type RelatedPage,
} from "@/components/service-page/ServicePageSections";

const KEYWORDS = [
  "group tuition classes", "group coaching classes", "online group classes",
  "group tuition classes online", "small group tutoring", "batch coaching classes",
];

const EXPLORE_GROUP = { label: "Explore Group Classes", to: "/search/group" };

const IMG = {
  what:     { src: "/bookmycoaching-group-teacher-leading-a-class.webp", alt: "Teacher leading a group coaching class", width: 960, height: 1080 },
  why:      { src: "/bookmycoaching-group-classmates-practising-together.webp", alt: "Classmates practising a topic together", width: 720, height: 540 },
  join:     { src: "/bookmycoaching-group-join-batches-on-bookmycoaching.webp", alt: "Joining group class batches on BookMyCoaching", width: 900, height: 560 },
  browse:   { src: "/bookmycoaching-group-student-comparing-batches.webp", alt: "Student comparing group class batches on a phone", width: 960, height: 720 },
  school:   { src: "/bookmycoaching-group-class-10-maths-group.webp", alt: "Class 10 maths group class", width: 600, height: 300 },
  exam:     { src: "/bookmycoaching-group-exam-preparation-batch.webp", alt: "Exam preparation batch", width: 600, height: 300 },
  skills:   { src: "/bookmycoaching-group-spoken-english-practice.webp", alt: "Spoken English practice", width: 600, height: 300 },
  fees:     { src: "/bookmycoaching-group-weekly-batch-timetable.webp", alt: "Weekly batch timetable", width: 900, height: 400 },
  vsGroup:  { src: "/bookmycoaching-group-group-class-discussion.webp", alt: "Group class discussion", width: 800, height: 450 },
  vsOne:    { src: "/bookmycoaching-group-one-on-one-lesson.webp", alt: "One-on-one lesson", width: 800, height: 450 },
  ready:    { src: "/bookmycoaching-group-students-learning-together.webp", alt: "Group of students learning together", width: 600, height: 600 },
  faq:      { src: "/bookmycoaching-group-students-with-questions.webp", alt: "Students with questions", width: 600, height: 450 },
};

const JUMP_ITEMS = [
  { id: "what",   label: "What is it?" },
  { id: "why",    label: "Benefits" },
  { id: "join",   label: "How to join" },
  { id: "browse", label: "Browse batches" },
  { id: "fees",   label: "Fees & timings" },
  { id: "vs",     label: "vs 1-on-1" },
  { id: "faq",    label: "FAQs" },
];

const RELATED: RelatedPage[] = [
  {
    label: "1-on-1 Tutoring",
    color: "#2F6BD8",
    icon: User,
    title: "Find 1-on-1 Private Tutors for Personalised Learning",
    excerpt: "One teacher, one student. Lessons built around your subject, level and schedule.",
    to: "/private-tutoring-sessions",
  },
  {
    label: "Crash Courses",
    color: "#D9472B",
    icon: Timer,
    title: "Exam Crash Courses — Fixed Sessions, Clear Timetable",
    excerpt: "Intensive programmes built around exam dates. One package price, defined session count.",
    to: "/exam-crash-courses",
  },
  {
    label: "Become a Tutor",
    color: "#262A7A",
    icon: BookOpen,
    title: "Become an Online Tutor in India with BookMyCoaching",
    excerpt: "Set your fees, choose your schedule and connect with students looking for your expertise.",
    to: "/become-an-online-tutor",
  },
];

const faqs: FaqItem[] = [
  {
    question: "What are group tuition classes?",
    answer: "Group tuition classes bring several students together to learn from one teacher in a batch. The batch follows a fixed timetable, with a set start date and seat limit.",
  },
  {
    question: "Are group classes more affordable than 1-on-1 tuition?",
    answer: "It depends on the teacher, since each one sets the fee. Group classes list a fee per student, so you can compare them with 1-on-1 listings and see which fits your budget. Always read the full listing first.",
  },
  {
    question: "How many students can join a group coaching batch?",
    answer: "Each teacher sets the seat limit for their own batch. You can see the limit and the available seats on the listing before you ask to join.",
  },
  {
    question: "Can I find online group coaching classes?",
    answer: "Yes. Listings show the teaching mode, so you can look for online group classes in your subject. Check the batch details to confirm the format.",
  },
  {
    question: "Can I choose a batch according to my schedule?",
    answer: "Yes. Every batch shows its start date and weekly timings. Compare a few batches and pick the one that fits your routine, then confirm the details with the teacher.",
  },
  {
    question: "How do I check group class fees and available seats?",
    answer: "Open the batch listing. It shows the fee per student, the seat limit and whether enrolment is open. You can also send an enquiry to the teacher with any questions.",
  },
];

export default function GroupClassesPage() {
  const { pathname } = useLocation();
  const title = 'Join Group Classes for School, Exams & Skills | BookMyCoaching';
  const description = 'Want to learn with others on a fixed schedule? Find group tuition classes led by teachers, with clear timings, seat limits and fees listed upfront.';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={getCanonicalUrl(pathname)} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={getCanonicalUrl(pathname)} />
        <meta property="og:image" content={getDefaultOgImage()} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Header />

      <main>
        <ServiceHero
          badge="Group Classes"
          badgeColor="#0E8A78"
          titleLead="Join Group Classes for"
          titleHighlight="School, Exams & Skills"
          intro="Learn alongside peers on a fixed schedule. Find group tuition classes with clear timings, seat limits and fees listed upfront — compare and enquire in minutes."
          primary={EXPLORE_GROUP}
          secondary={{ label: "How batches work", to: "#join" }}
          facts={[
            { icon: CalendarDays, label: "Fixed batch schedule" },
            { icon: Users,        label: "Peer learning" },
            { icon: BadgeCheck,   label: "Teacher-set fees" },
          ]}
          image="/bookmycoaching-group-classes.gif"
        />

        <JumpNav items={JUMP_ITEMS} />

        {/* What */}
        <SplitSection
          id="what"
          title="What are Group Coaching Classes?"
          imageLeft
          image={IMG.what}
          paras={[
            "Group coaching classes bring several students together for lessons led by one teacher. Each batch follows a shared schedule.",
            "The teacher sets the start date, the seat limit and the fee per student. All of it is shown on the listing, so you know the plan before you ask to join.",
          ]}
          bullets={[
            "Fixed start date and recurring weekly schedule",
            "Seat limit and per-student fee set by the teacher",
            "Teacher confirmation for each enrolment request",
            "Available online or offline, depending on the listing",
          ]}
          paras2={[
            "BookMyCoaching lists batches from different teachers in one place, so comparing group coaching classes takes minutes, not days. A fixed timetable removes the guesswork about when to study.",
          ]}
        />

        {/* Benefits */}
        <CardsSection
          id="why"
          title="Benefits of Learning in Group Classes"
          imageSide="right"
          image={IMG.why}
          intro="A group setting can support steady learning through planned sessions and time with classmates."
          shaded
          cards={[
            { icon: Users,          title: "Peer learning",           description: "When others ask questions, you often find answers to doubts you had not spoken aloud." },
            { icon: CalendarDays,   title: "Clear timetable",         description: "A fixed weekly schedule keeps you consistent. No guessing when to study." },
            { icon: ListChecks,     title: "Shared goals",            description: "A batch focused on the same subject or exam keeps everyone motivated together." },
            { icon: Award,          title: "Small-batch option",      description: "Some teachers keep batches limited — fewer seats means more room for your questions." },
            { icon: MessageSquare,  title: "Group discussion",        description: "Hear different questions and explanations. Classmates often spot things you miss alone." },
            { icon: GraduationCap,  title: "Exam-focused batches",   description: "Many batches are built around a specific exam or level, keeping the content relevant." },
          ]}
          after={[
            "The same benefits apply to online group classes, where you join the batch from home and still learn with a real class.",
          ]}
        />

        {/* How to join */}
        <StepsSection
          id="join"
          title="How to Join a Group Class on BookMyCoaching"
          image={IMG.join}
          intro="Before you ask to join, check that the timetable, subject level and fee fit your needs."
          steps={[
            { title: "Filter batches",   description: "Choose a subject or exam and filter for Group Classes." },
            { title: "Review the batch", description: "Check batch details, schedule and available seats." },
            { title: "Compare options",  description: "Compare the teacher profile, level and per-student fee." },
            { title: "Send an enquiry",  description: "Log in or create a free account to enquire about enrolment." },
          ]}
          after={[
            "Take a few minutes on step 3. A teacher whose level and style match yours will make every class feel easier. Once you send your enquiry, the teacher reviews your request and confirms your seat.",
          ]}
        />

        {/* Browse */}
        <ChipsSection
          id="browse"
          title="Browse Group Classes by Subject or Exam"
          image={IMG.browse}
          intro="Find batches in your subject or exam area. What you see depends on the classes teachers have published right now."
          shaded
          groups={[
            {
              icon: BookOpen,
              title: "School subjects",
              image: IMG.school,
              chips: [
                { label: "Class 10 Maths",    to: "/search?subject=maths&format=group&level=10" },
                { label: "Class 12 Physics",  to: "/search?subject=physics&format=group&level=12" },
                { label: "Class 12 Chemistry",to: "/search?subject=chemistry&format=group&level=12" },
                { label: "English",           to: "/search?subject=english&format=group" },
              ],
            },
            {
              icon: ListChecks,
              title: "Exam preparation",
              image: IMG.exam,
              chips: [
                { label: "JEE Main",  to: "/search?exam=JEE&format=group" },
                { label: "NEET",     to: "/search?exam=NEET&format=group" },
                { label: "UPSC",     to: "/search?exam=UPSC&format=group" },
                { label: "CAT",      to: "/search?exam=CAT&format=group" },
                { label: "IELTS",    to: "/search?exam=IELTS&format=group" },
              ],
            },
            {
              icon: MessageSquare,
              title: "Skills & languages",
              image: IMG.skills,
              chips: [
                { label: "Spoken English", to: "/search?subject=spoken-english&format=group" },
                { label: "Coding",         to: "/search?subject=coding&format=group" },
                { label: "French",         to: "/search?subject=french&format=group" },
              ],
            },
          ]}
          after={[
            "Prefer to study from home? Search for online group classes in your subject and compare the batches. Many students like group tuition classes online because there is no travel, and the lesson still runs on a fixed schedule.",
          ]}
        />

        {/* Fees panel */}
        <PanelSection
          id="fees"
          title="Group Class Fees, Batch Size and Timings"
          image={IMG.fees}
          level="h3"
          intro="Teachers control batch pricing, seat limits and lesson timings. Different classes may have different fee structures and enrolment limits."
          items={[
            "Price: Review the fee per student, per session or per batch",
            "Schedule: Check the batch start date and recurring weekly schedule",
            "Seats: Confirm the seat limit and whether enrolment is open",
            "Timings: Check the class length and weekly days against your routine",
          ]}
          boxTitle="Check on every batch:"
        />

        {/* Compare */}
        <CompareSection
          id="vs"
          title="Group Classes vs Private Tutoring"
          level="h3"
          intro="Your preferred learning environment can help you choose the right format."
          shaded
          cards={[
            {
              icon: Users,
              title: "Group Classes",
              description: "Shared lessons, a fixed batch schedule and peer interaction. Fee per student — often lower than 1-on-1.",
              current: true,
              image: IMG.vsGroup,
            },
            {
              icon: User,
              title: "1-on-1 Sessions",
              description: "One student with one teacher, tailored to individual needs. Flexible scheduling, lessons at your own pace.",
              link: { label: "Find Private Tutors", to: "/private-tutoring-sessions" },
              image: IMG.vsOne,
            },
          ]}
          after={[
            "If you enjoy learning with others and like a set routine, group tuition classes are a natural fit. Small group tutoring sits between the two — a limited batch still gives you shared learning with more room for questions.",
          ]}
        />

        <CtaBlock
          title="Find a Group Class That Fits Your Goals"
          body="Check out the live batches and talk to a teacher about a class that matches your schedule and subject. Whether you want group tuition classes online or a batch near you, the details are on each listing."
          cta={EXPLORE_GROUP}
          image={IMG.ready}
        />

        <FaqSection faqs={faqs} cta={EXPLORE_GROUP} image={IMG.faq} />

        <RelatedPages pages={RELATED} />
      </main>
      <Footer />
    </div>
  );
}
